/**
 * Funções puras de projeto: BPM, marcadores automáticos, punch de takes e
 * documentos padrão. Tudo testável sem navegador.
 */
import type {
  CompingRegion,
  ExportSettings,
  Marker,
  MixSettings,
  ProjectDoc,
  RecordedSegment,
  SourceType,
  SyncSettings,
  Take,
  Track,
  TrackKind,
} from '../types.js';
import { defaultFx } from './fx.js';
import { median, uid, clamp } from '../utils/misc.js';
import { mergeSegments } from './buffers.js';

export const TRACK_META: Record<TrackKind, { name: string; color: string; order: number }> = {
  lead: { name: 'VOCAL PRINCIPAL', color: '#ff3b5c', order: 0 },
  backing: { name: 'BACKING VOCAL', color: '#3ddc97', order: 1 },
  double: { name: 'DOBRA', color: '#f5c451', order: 2 },
  adlib: { name: 'ADLIBS', color: '#7aa2ff', order: 3 },
  harmony1: { name: 'HARMONIA 1', color: '#c07aff', order: 4 },
  harmony2: { name: 'HARMONIA 2', color: '#ff8ad4', order: 5 },
};

export function defaultSync(): SyncSettings {
  return {
    latencyOffsetMs: 0,
    inputLatencyMs: 0,
    outputLatencyMs: 0,
    playerStartDelayMs: 0,
    driftCorrectionMs: 0,
    driftPpm: 0,
    driftThresholdMs: 45,
    autoSyncEnabled: true,
  };
}

export function defaultMix(): MixSettings {
  return {
    masterVolume: 1,
    masterLimitDb: -1.2,
    instrumentalVolume: 0.9,
    instrumentalPan: 0,
    instrumentalMuted: false,
    instrumentalSolo: false,
    fadeInMs: 12,
    fadeOutMs: 0,
    monitorVolume: 0.9,
  };
}

export function defaultExportSettings(): ExportSettings {
  return {
    format: 'wav',
    quality: 'alta',
    sampleRate: 48000,
    bitDepth: 24,
    normalize: true,
    normalizePeakDb: -1,
    limiter: true,
    includeInstrumental: true,
    dither: true,
  };
}

export function createTrack(kind: TrackKind, offsetMs = 0): Track {
  const meta = TRACK_META[kind];
  return {
    id: uid('track'),
    kind,
    name: meta.name,
    comping: false,
    regionMap: [],
    offsetMs,
    volume: kind === 'lead' ? 1 : 0.75,
    pan: kind === 'double' ? 0.25 : 0,
    muted: false,
    solo: false,
    armed: kind === 'lead',
    fx: defaultFx(),
    color: meta.color,
    createdAt: Date.now(),
    peaks: null,
  };
}

export function createProject(opts: {
  title?: string;
  sourceType: SourceType;
  youtubeVideoId?: string;
  youtubeTitle?: string;
}): ProjectDoc {
  const lead = createTrack('lead');
  return {
    id: uid('proj'),
    title: opts.title ?? 'Novo projeto',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    sourceType: opts.sourceType,
    youtubeVideoId: opts.youtubeVideoId,
    youtubeTitle: opts.youtubeTitle,
    instrumental: null,
    tracks: [lead],
    takes: [],
    markers: [],
    notes: [],
    sync: defaultSync(),
    syncEvents: [],
    mix: defaultMix(),
    exportSettings: defaultExportSettings(),
    ui: {
      mode: 'easy',
      countdown: 'sec3',
      monitor: false,
      recordingGainBoostPercent: 0,
      lyrics: '',
      prompterSpeed: 1,
      prompterFont: 34,
      bpm: 90,
      metronome: false,
      metronomeVolume: 0.5,
      key: '',
      speed: 1,
    },
    analysis: null,
    cover: null,
    version: 1,
  };
}

/**
 * Estima o BPM a partir dos ataques detectados, testando o intervalo mediano
 * e seus múltiplos/submúltiplos dentro de 60..180 BPM.
 */
export function estimateBpm(onsetTimes: number[], fallback = 90): { bpm: number; confidence: number } {
  if (onsetTimes.length < 6) return { bpm: fallback, confidence: 0 };
  const intervals: number[] = [];
  for (let i = 1; i < onsetTimes.length; i++) {
    const d = onsetTimes[i] - onsetTimes[i - 1];
    if (d > 0.08 && d < 3) intervals.push(d);
  }
  if (intervals.length < 4) return { bpm: fallback, confidence: 0 };
  const med = median(intervals);
  let bpm = 60 / med;
  while (bpm < 60) bpm *= 2;
  while (bpm > 180) bpm /= 2;
  // confiança: quão regulares são os intervalos
  const dev = intervals.map((i) => Math.abs(i - med) / med);
  const rel = median(dev);
  const confidence = clamp(1 - rel * 2.2, 0, 0.99);
  return { bpm: Math.round(bpm), confidence };
}

/** Marcadores automáticos proporcionais (o usuário edita/renomeia depois). */
export function autoMarkers(duration: number, opts: { bpm?: number; onsetTimes?: number[] } = {}): Marker[] {
  if (duration <= 1) return [];
  const template: { kind: Marker['kind']; label: string; at: number }[] = [
    { kind: 'intro', label: 'INTRO', at: 0 },
    { kind: 'verso', label: 'VERSO 1', at: 0.08 },
    { kind: 'pre-refrao', label: 'PRÉ-REFRÃO', at: 0.26 },
    { kind: 'refrao', label: 'REFRÃO', at: 0.34 },
    { kind: 'verso', label: 'VERSO 2', at: 0.48 },
    { kind: 'refrao', label: 'REFRÃO 2', at: 0.64 },
    { kind: 'ponte', label: 'PONTE', at: 0.78 },
    { kind: 'final', label: 'FINAL', at: 0.93 },
  ];
  return template
    .filter((t) => t.at * duration < duration - 0.5)
    .map((t) => ({
      id: uid('marker'),
      time: Math.round(t.at * duration * 100) / 100,
      label: t.label,
      kind: t.kind,
    }));
}

/**
 * Punch-in/out: substitui o trecho de um take existente pelo take novo.
 * Retorna um NOVO take (o original é preservado na lista para comparação).
 */
export function buildPunchedTake(
  base: Take,
  patch: Take,
  region: { start: number; end: number },
  sampleRate: number,
  index: number
): Take {
  const baseAudio = mergeSegments(base.segments, { sampleRate, start: 0, duration: Math.max(region.end + 0.5, base.recordedSeconds + 0.5) });
  const patchAudio = mergeSegments(patch.segments, { sampleRate, start: 0, duration: region.end + 0.5 });
  const startIdx = Math.max(0, Math.round(region.start * sampleRate));
  const endIdx = Math.min(patchAudio.data.length, Math.round(region.end * sampleRate));
  const out = Float32Array.from(baseAudio.data.length >= endIdx ? baseAudio.data : growTo(baseAudio.data, endIdx));
  const cf = Math.round(0.012 * sampleRate);
  for (let i = startIdx; i < endIdx; i++) {
    const inFade = cf > 0 && i - startIdx < cf ? (i - startIdx) / cf : 1;
    const outFade = cf > 0 && endIdx - i < cf ? (endIdx - i) / cf : 1;
    const g = Math.min(inFade, outFade);
    out[i] = out[i] * (1 - g) + (patchAudio.data[i] ?? 0) * g;
  }
  let p = 0;
  for (let i = 0; i < out.length; i++) p = Math.max(p, Math.abs(out[i]));
  const segment: RecordedSegment = {
    id: uid('seg'),
    position: 0,
    samples: out,
    sampleRate,
    startedAtCtx: patch.segments[0]?.startedAtCtx ?? 0,
    startedAtPerf: patch.segments[0]?.startedAtPerf ?? 0,
    mediaRequestedAtPerf: patch.segments[0]?.mediaRequestedAtPerf ?? 0,
    inputLatencySec: patch.segments[0]?.inputLatencySec ?? 0,
    peak: p,
    clipped: p >= 0.999,
    label: `Punch ${region.start.toFixed(1)}–${region.end.toFixed(1)}s`,
  };
  return {
    ...patch,
    id: uid('take'),
    index,
    name: `Take ${index} (punch ${formatSec(region.start)}→${formatSec(region.end)})`,
    segments: [segment],
    recordedSeconds: out.length / sampleRate,
    mediaStart: base.mediaStart,
    peak: p,
    clipped: p >= 0.999,
    createdAt: Date.now(),
  };
}

function growTo(data: Float32Array, length: number): Float32Array {
  if (data.length >= length) return data;
  const out = new Float32Array(length);
  out.set(data);
  return out;
}

function formatSec(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Regiões de comping sugeridas a partir dos marcadores. */
export function compingFromMarkers(markers: Marker[], takes: Take[], duration: number): CompingRegion[] {
  if (!takes.length || duration <= 0) return [];
  const bounds = [...markers.map((m) => m.time), duration].sort((a, b) => a - b);
  const out: CompingRegion[] = [];
  let i = 0;
  for (let k = 0; k < bounds.length - 1; k++) {
    const start = bounds[k];
    const end = bounds[k + 1];
    if (end - start < 0.25) continue;
    const take = takes[i % takes.length];
    out.push({ id: uid('region'), start, end, takeId: take.id, label: markers.find((m) => Math.abs(m.time - start) < 0.01)?.label });
    i++;
  }
  return out;
}
